"use client";

import { useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import useSolopreneurProfile from "@/hooks/useSolopreneurProfile";
import { moisDeLAnnee, outilsQuiLisent, rythmeDesEncaissements } from "@/lib/tools/dossier";
import { situationDuDossier } from "@/lib/tools/dossierSituation";
import { euros } from "@/lib/tools/dossierTextes";
import { Avancer, Champ, Choix, Ecran, useParcours } from "./Journey";

/**
 * Les six questions du dossier, une par écran.
 *
 * CHAQUE RÉPONSE EST GARDÉE AUSSITÔT. Celui qui s'arrête à la troisième
 * question a déjà trois réponses que les outils reprennent : attendre la
 * dernière pour tout écrire perdrait tout ce qu'il a dit avant de partir.
 *
 * TOUTES SE PASSENT. Le dossier ne demande rien d'obligatoire : une question
 * sautée laisse le champ vide, et les outils la reposent le moment venu.
 *
 * CHAQUE QUESTION DIT À QUOI ELLE SERT, en nombre d'outils. Ce nombre est
 * calculé (outilsQuiLisent), pas écrit : il suit les outils quand ils changent.
 *
 * `seule` ouvre une seule question, pour la corriger depuis la page de
 * situation : on y revient dès qu'elle est répondue.
 */

/**
 * Une phase par question, comme sur la maquette : « Question 4 sur 6 ». Les
 * sous-questions (l'imposition d'une EURL, l'inscription comme artisan)
 * restent dans la phase de leur question.
 */
const PHASES = {
    statut: 0,
    eurlFiscal: 0,
    activite: 1,
    artisan: 1,
    debut: 2,
    ca: 3,
    foyer: 4,
    objectif: 5,
};

const ORDRE = ["statut", "activite", "debut", "ca", "foyer", "objectif"];

const T = {
    fr: {
        phases: [1, 2, 3, 4, 5, 6].map((n) => `Question ${n} sur 6`),
        retour: "Retour",
        passer: "Passer cette question",
        annuler: "Annuler",
        continuer: "Continuer",
        terminer: "Voir ma situation",
        enregistrer: "Enregistrer",
        pourquoi: "Pourquoi on vous le demande",

        qStatut: "Où en êtes-vous ?",
        qStatutAide: "Choisissez ce qui vous décrit aujourd'hui. Vous pourrez le changer à tout moment.",
        projet: "J'ai un projet",
        projetDetail: "Rien n'est encore déclaré.",
        micro: "Je suis micro-entrepreneur",
        microDetail: "Auto-entrepreneur, c'est la même chose.",
        reel: "Entreprise individuelle au réel",
        reelDetail: "Vous déduisez vos vraies dépenses.",
        eurl: "J'ai une EURL",
        eurlDetail: "On vous demandera ensuite comment elle est imposée.",
        sasu: "J'ai une SASU",
        sasuDetail: "Vous en êtes le président.",
        pStatut: "Les règles changent d'un statut à l'autre. Cette réponse remplit {outils}.",

        qEurl: "Comment votre EURL est-elle imposée ?",
        qEurlAide: "C'est écrit dans vos statuts, ou dans l'option envoyée aux impôts à la création.",
        eurlIs: "À l'impôt sur les sociétés",
        eurlIsDetail: "La société paie l'impôt, vous vous versez une rémunération et des dividendes.",
        eurlIr: "À l'impôt sur le revenu",
        eurlIrDetail: "Le bénéfice s'ajoute à vos revenus, comme en entreprise individuelle.",
        eurlInconnu: "Je ne sais pas",
        eurlInconnuDetail: "Presque toutes les EURL choisissent l'impôt sur les sociétés : nous partirons de là.",

        qActivite: "Que vendez-vous ?",
        qActiviteAide: "Si vous faites plusieurs choses, choisissez celle qui rapporte le plus.",
        vente: "Des marchandises",
        venteDetail: "Achat-revente, fabrication, restauration.",
        services: "Des prestations commerciales ou artisanales",
        servicesDetail: "Réparation, ménage, livraison, coiffure.",
        bnc: "Une activité libérale",
        bncDetail: "Conseil, développement, design, formation.",
        cipav: "Une profession libérale réglementée",
        cipavDetail: "Architecte, psychologue, ostéopathe.",
        pActivite: "C'est elle qui fixe vos cotisations et vos plafonds. Cette réponse remplit {outils}.",

        qArtisan: "Êtes-vous inscrit comme artisan ?",
        qArtisanAide: "Au registre national des entreprises, avec la chambre de métiers et de l'artisanat.",
        artisanOui: "Oui, je suis artisan",
        artisanOuiDetail: "Plombier, coiffeur, boulanger, électricien.",
        artisanNon: "Non",
        artisanNonDetail: "Commerçant ou prestataire sans métier de l'artisanat.",

        qDebut: "Depuis quand êtes-vous en activité ?",
        qDebutAideMicro: "La date de début que vous avez déclarée. Elle figure sur votre avis de situation Sirene.",
        qDebutAideSociete: "La date d'immatriculation de la société, sur son extrait Kbis.",
        qDebutProjet: "Quand pensez-vous démarrer ?",
        qDebutProjetAide: "Une date approchée suffit : elle sert à calculer vos échéances de première année.",
        dateDebut: "Date de début",
        pDebut: "L'ACRE, la CFE et les plafonds de la première année en dépendent. Cette réponse remplit {outils}.",

        qCa: "Combien encaissez-vous par an ?",
        qCaAide: "Ce que vos clients vous paient, hors taxes. Pas ce qu'il vous reste.",
        qCaProjet: "Combien pensez-vous encaisser par an ?",
        qCaProjetAide: "Une estimation, hors taxes. Elle se corrige à tout moment.",
        qCaSociete: "Quel chiffre d'affaires fait votre société ?",
        caCetteAnnee: "Cette année, ce que vous prévoyez",
        caAnDernier: "L'an dernier",
        caAnDernierAide: "Laissez vide si c'est votre première année.",
        pCa: "Cette réponse remplit {outils}. Elle sert aussi à vous prévenir avant un seuil ou un plafond.",
        qCaMois: "Qu'avez-vous encaissé cette année ?",
        qCaMoisSociete: "Qu'a encaissé votre société cette année ?",
        qCaMoisAide: "Mois par mois, hors taxes. Un ordre de grandeur suffit, vous corrigerez au fil des mois.",
        enCours: "en cours",
        totalMois: "Encaissé depuis {mois}",
        versTotal: "Je n'ai pas le détail : donner un total pour l'année",
        versMois: "Détailler mois par mois",
        pCaMois: "Avec vos vrais mois, la date du seuil de TVA se calcule sur ce que vous avez encaissé, pas sur une moyenne.",
        apercuRythme: "À ce rythme : {rythme} sur l'année.",

        qFoyer: "Et votre foyer ?",
        qFoyerAide: "Votre impôt se calcule sur tout le foyer, pas seulement sur votre activité.",
        declarez: "Vous déclarez vos impôts",
        seul: "Seul",
        couple: "En couple",
        enfants: "Enfants à charge",
        enfantMoins: "Un enfant de moins",
        enfantPlus: "Un enfant de plus",
        autresRevenus: "Autres revenus du foyer, par an",
        autresRevenusAide: "Un salaire, le vôtre ou celui de votre conjoint. Laissez 0 s'il n'y en a pas.",
        pFoyer: "Sans ces réponses, l'impôt affiché serait faux. Cette réponse remplit {outils}.",

        qObjectif: "Combien voulez-vous gagner par mois ?",
        qObjectifAide: "Ce que vous aimeriez garder pour vivre, une fois tout payé.",
        objectif: "Par mois, pour vous",
        frais: "Vos frais, par mois",
        fraisAide: "Logiciels, matériel, déplacements, assurance.",
        pObjectif: "Vous saurez s'il vous manque de l'argent chaque mois, et combien facturer pour y arriver. Cette réponse remplit {outils}.",
        apercuCa: "À ce chiffre : environ {reste} par mois pour vous, avant vos frais.",
        apercuTva: "Vous seriez à {part} du seuil de TVA.",
        apercuManque: "Avec ce que vous encaissez, il vous manquerait environ {manque} par mois, frais payés.",
        apercuAtteint: "Avec ce que vous encaissez, cet objectif est atteint, frais payés.",

        euro: "€",
    },
    en: {
        phases: [1, 2, 3, 4, 5, 6].map((n) => `Question ${n} of 6`),
        retour: "Back",
        passer: "Skip this question",
        annuler: "Cancel",
        continuer: "Continue",
        terminer: "See my situation",
        enregistrer: "Save",
        pourquoi: "Why we ask",

        qStatut: "Where are you at?",
        qStatutAide: "Pick what describes you today. You can change it at any time.",
        projet: "I have a project",
        projetDetail: "Nothing is registered yet.",
        micro: "I am a micro-entrepreneur",
        microDetail: "Auto-entrepreneur is the same thing.",
        reel: "Sole trader on actual expenses",
        reelDetail: "You deduct your real expenses.",
        eurl: "I have an EURL",
        eurlDetail: "We will then ask how it is taxed.",
        sasu: "I have a SASU",
        sasuDetail: "You are its president.",
        pStatut: "The rules change from one status to another. This answer fills in {outils}.",

        qEurl: "How is your EURL taxed?",
        qEurlAide: "It is in your articles of association, or in the option sent to the tax office at creation.",
        eurlIs: "Corporation tax",
        eurlIsDetail: "The company pays the tax, you pay yourself a salary and dividends.",
        eurlIr: "Income tax",
        eurlIrDetail: "The profit is added to your income, as for a sole trader.",
        eurlInconnu: "I don't know",
        eurlInconnuDetail: "Almost every EURL chooses corporation tax: we will start from there.",

        qActivite: "What do you sell?",
        qActiviteAide: "If you do several things, pick the one that earns the most.",
        vente: "Goods",
        venteDetail: "Resale, manufacturing, catering.",
        services: "Commercial or craft services",
        servicesDetail: "Repairs, cleaning, delivery, hairdressing.",
        bnc: "A liberal profession",
        bncDetail: "Consulting, development, design, training.",
        cipav: "A regulated liberal profession",
        cipavDetail: "Architect, psychologist, osteopath.",
        pActivite: "It sets your contributions and ceilings. This answer fills in {outils}.",

        qArtisan: "Are you registered as a craftsperson?",
        qArtisanAide: "In the national business register, with the chamber of trades and crafts.",
        artisanOui: "Yes, I am a craftsperson",
        artisanOuiDetail: "Plumber, hairdresser, baker, electrician.",
        artisanNon: "No",
        artisanNonDetail: "Trader or service provider without a craft trade.",

        qDebut: "Since when have you been in business?",
        qDebutAideMicro: "The start date you declared. It is on your Sirene registration notice.",
        qDebutAideSociete: "The company's registration date, on its Kbis extract.",
        qDebutProjet: "When do you plan to start?",
        qDebutProjetAide: "An approximate date is enough: it is used to work out your first-year deadlines.",
        dateDebut: "Start date",
        pDebut: "The ACRE, the CFE and the first-year ceilings depend on it. This answer fills in {outils}.",

        qCa: "How much do you bring in per year?",
        qCaAide: "What your clients pay you, excluding VAT. Not what you keep.",
        qCaProjet: "How much do you expect to bring in per year?",
        qCaProjetAide: "An estimate, excluding VAT. You can correct it at any time.",
        qCaSociete: "What is your company's revenue?",
        caCetteAnnee: "This year, what you expect",
        caAnDernier: "Last year",
        caAnDernierAide: "Leave it empty if this is your first year.",
        pCa: "This answer fills in {outils}. It is also used to warn you before a threshold or a ceiling.",
        qCaMois: "What have you brought in this year?",
        qCaMoisSociete: "What has your company brought in this year?",
        qCaMoisAide: "Month by month, excluding VAT. A rough figure is enough, you will correct it as the months go by.",
        enCours: "so far",
        totalMois: "Brought in since {mois}",
        versTotal: "I don't have the detail: give a total for the year",
        versMois: "Break it down by month",
        pCaMois: "With your real months, the date you cross the VAT threshold is worked out from what you brought in, not from an average.",
        apercuRythme: "At this pace: {rythme} over the year.",

        qFoyer: "And your household?",
        qFoyerAide: "Your income tax is worked out on the whole household, not just your business.",
        declarez: "You file your tax return",
        seul: "Single",
        couple: "As a couple",
        enfants: "Dependent children",
        enfantMoins: "One child fewer",
        enfantPlus: "One child more",
        autresRevenus: "Other household income, per year",
        autresRevenusAide: "A salary, yours or your partner's. Leave 0 if there is none.",
        pFoyer: "Without these answers, the tax shown would be wrong. This answer fills in {outils}.",

        qObjectif: "How much do you want to earn per month?",
        qObjectifAide: "What you would like to keep to live on, once everything is paid.",
        objectif: "Per month, for you",
        frais: "Your expenses, per month",
        fraisAide: "Software, equipment, travel, insurance.",
        pObjectif: "You will know whether you fall short each month, and how much to charge to get there. This answer fills in {outils}.",
        apercuCa: "At this figure: about {reste} a month for you, before your expenses.",
        apercuTva: "You would be at {part} of the VAT threshold.",
        apercuManque: "With what you bring in, about {manque} a month would be missing, expenses paid.",
        apercuAtteint: "With what you bring in, this goal is reached, expenses paid.",

        euro: "€",
    },
};

/** Le nombre d'outils de chaque question, calculé une fois au chargement du module. */
const OUTILS_PAR_QUESTION = {
    statut: outilsQuiLisent(["statut"]).length,
    activite: outilsQuiLisent(["activite", "artisan"]).length,
    debut: outilsQuiLisent(["dateDebut"]).length,
    ca: outilsQuiLisent(["caCetteAnnee", "caAnDernier"]).length,
    foyer: outilsQuiLisent(["enCouple", "enfants", "autresRevenus"]).length,
    objectif: outilsQuiLisent(["objectifMensuel", "fraisMensuels"]).length,
};

function nOutils(t, lang, n) {
    const mot = lang === "en" ? (n === 1 ? "tool" : "tools") : n === 1 ? "outil" : "outils";
    return `${n} ${mot}`;
}

function remplir(texte, valeurs) {
    return texte.replace(/\{(\w+)\}/g, (_, k) => (valeurs[k] !== undefined ? valeurs[k] : `{${k}}`));
}

/** Un montant tapé : chiffres seuls, vide permis. */
function lireMontant(texte) {
    // Les centimes tombent : « 48 000,50 » vaut 48 000, et non 4 800 050.
    const entier = String(texte ?? "").trim().replace(/[.,]\d{1,2}\s*€?$/, "");
    const chiffres = entier.replace(/\D/g, "");
    return chiffres === "" ? null : Number(chiffres);
}

/**
 * Un écran de saisie est un vrai formulaire : la touche Entrée, ou « OK » du
 * clavier d'un téléphone, valide comme le bouton.
 */
function soumettre(valider, desactive) {
    return (e) => {
        e.preventDefault();
        if (!desactive) valider();
    };
}

/**
 * CE QUE LA RÉPONSE DONNE, PENDANT QU'ON LA TAPE. Un aperçu recalculé à
 * chaque frappe, par les mêmes moteurs que la page de situation : on voit
 * le gain de la question avant de l'avoir validée.
 */
function Apercu({ lignes }) {
    if (!lignes.length) return null;
    return (
        <div aria-live="polite" className="mt-4 rounded-xl bg-blue-50 dark:bg-blue-500/10 px-4 py-3 text-sm leading-6 text-blue-900 dark:text-blue-200">
            {lignes.map((l) => (
                <p key={l} className="mb-0">
                    {l}
                </p>
            ))}
        </div>
    );
}

function Pourquoi({ texte, titre }) {
    return (
        <div className="mt-6 flex gap-2.5 rounded-xl bg-blue-50 dark:bg-blue-500/10 px-4 py-3 text-sm leading-6 text-blue-900 dark:text-blue-200">
            <SolarIcon name="info-circle-linear" className="mt-1 h-4 w-4 shrink-0" />
            <p className="mb-0">
                <span className="sr-only">{titre} : </span>
                {texte}
            </p>
        </div>
    );
}

function Passer({ onClick, libelle }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="mt-4 block text-sm text-gray-500 dark:text-lightBlue/70 underline underline-offset-2 decoration-gray-300 dark:decoration-white/20 hover:text-primary transition-colors duration-200"
        >
            {libelle}
        </button>
    );
}

function Grille({ choix }) {
    return (
        <div className={`grid gap-3 ${choix.length >= 3 ? "sm:grid-cols-2" : "sm:grid-cols-2"}`}>
            {choix.map((c) => (
                <Choix key={c.titre} {...c} />
            ))}
        </div>
    );
}

export default function ToolsDossierQuestions({ lang = "fr", depart = "statut", seule = false, onTermine, onQuitter }) {
    const t = T[lang] || T.fr;
    const { profil, enregistrer } = useSolopreneurProfile();
    const maintenant = new Date();
    const annee = maintenant.getFullYear();
    const moisEnCours = maintenant.getMonth() + 1;
    const prefixeAnnee = `${annee}-`;

    // Les montants se tapent en texte, et ne s'écrivent qu'à « Continuer ».
    const texteDe = (v) => (v === null || v === undefined ? "" : String(v));
    const [saisie, setSaisie] = useState(() => ({
        caCetteAnnee: texteDe(profil.caCetteAnnee),
        caAnDernier: texteDe(profil.caAnDernier),
        autresRevenus: texteDe(profil.autresRevenus),
        objectifMensuel: texteDe(profil.objectifMensuel),
        fraisMensuels: texteDe(profil.fraisMensuels),
        dateDebut: profil.dateDebut || "",
        mois: Object.fromEntries(
            Object.entries(profil.encaissements || {})
                .filter(([m]) => m.startsWith(prefixeAnnee))
                .map(([m, v]) => [m, String(v)]),
        ),
    }));
    const poserSaisie = (champ) => (v) => setSaisie((s) => ({ ...s, [champ]: v }));
    const poserMois = (mois) => (v) => setSaisie((s) => ({ ...s, mois: { ...s.mois, [mois]: v } }));

    // LES ENCAISSEMENTS D'ABORD, LE TOTAL EN REPLI. Celui qui a déjà donné un
    // total, sans les mois, le retrouve tel quel ; les autres commencent par
    // la grille des mois.
    const [parMois, setParMois] = useState(
        () => Object.keys(profil.encaissements || {}).some((m) => m.startsWith(prefixeAnnee)) || profil.caCetteAnnee === null,
    );
    const moisPossibles = moisDeLAnnee(annee, moisEnCours, profil.dateDebut);
    const moisTapes = Object.fromEntries(
        moisPossibles.map((m) => [m, lireMontant(saisie.mois[m])]).filter(([, v]) => v !== null),
    );
    const rythme = rythmeDesEncaissements(moisTapes, annee, profil.dateDebut);

    const { etape, retour, aller, communs } = useParcours(depart, null, PHASES);
    const communsEcran = {
        ...communs,
        phases: t.phases,
        retour: t.retour,
        // À la première question, le retour ramène d'où l'on vient : la page
        // de situation, ou son accueil.
        onRetour: etape === depart ? onQuitter : retour,
    };
    const pourquoi = (cle, question) =>
        remplir(t[cle], { outils: nOutils(t, lang, OUTILS_PAR_QUESTION[question]) });

    /** La question suivante, ou la fin : en mode « seule », on repart aussitôt. */
    const suivante = (question) => {
        if (seule) {
            onTermine?.();
            return;
        }
        const i = ORDRE.indexOf(question);
        if (i < 0 || i === ORDRE.length - 1) {
            onTermine?.();
            return;
        }
        aller(ORDRE[i + 1]);
    };

    const statut = profil.statut;

    // Les aperçus, recalculés à chaque frappe, seulement quand le dossier en
    // sait assez pour qu'ils soient justes.
    // La grille des mois ne vaut qu'une fois l'activité commencée, et s'il y a
    // au moins un mois à remplir cette année.
    const grille = parMois && statut !== "projet" && moisPossibles.length > 0;
    const caTape = grille ? (rythme ? rythme.rythme : null) : lireMontant(saisie.caCetteAnnee);
    const apercuCa = (() => {
        if (etape !== "ca" || caTape === null || !profil.activite) return [];
        const x = situationDuDossier({ ...profil, caCetteAnnee: caTape, ...(grille ? { encaissements: moisTapes } : {}) });
        const lignes = grille ? [remplir(t.apercuRythme, { rythme: euros(caTape, lang) })] : [];
        if (x.revenu) lignes.push(remplir(t.apercuCa, { reste: euros(x.revenu.mensuel, lang) }));
        if (x.tva && x.tva.base) {
            lignes.push(
                remplir(t.apercuTva, {
                    part: new Intl.NumberFormat(lang === "en" ? "en-GB" : "fr-FR", { style: "percent", maximumFractionDigits: 0 }).format(caTape / x.tva.base),
                }),
            );
        }
        return lignes;
    })();
    const objectifTape = lireMontant(saisie.objectifMensuel);
    const apercuObjectif = (() => {
        if (etape !== "objectif" || objectifTape === null || profil.caCetteAnnee === null || !profil.activite) return [];
        const x = situationDuDossier({ ...profil, objectifMensuel: objectifTape, fraisMensuels: lireMontant(saisie.fraisMensuels) ?? 0 });
        if (!x.revenu || x.revenu.ecartMensuel === null) return [];
        return [
            x.revenu.ecartMensuel < -50
                ? remplir(t.apercuManque, { manque: euros(Math.round(-x.revenu.ecartMensuel / 10) * 10, lang) })
                : t.apercuAtteint,
        ];
    })();
    const enSociete = statut === "eurl" || statut === "eurlIr" || statut === "sasu";

    if (etape === "statut") {
        const choisir = (s) => () => {
            if (s === "eurl") {
                aller("eurlFiscal");
                return;
            }
            enregistrer({ statut: s });
            suivante("statut");
        };
        return (
            <Ecran key={etape} {...communsEcran} phase={PHASES.statut} titre={t.qStatut} aide={t.qStatutAide}>
                <Grille
                    choix={[
                        { icone: "lightbulb-linear", titre: t.projet, detail: t.projetDetail, onClick: choisir("projet") },
                        { icone: "user-linear", titre: t.micro, detail: t.microDetail, onClick: choisir("micro") },
                        { icone: "document-text-linear", titre: t.reel, detail: t.reelDetail, onClick: choisir("reel") },
                        { icone: "buildings-2-linear", titre: t.eurl, detail: t.eurlDetail, onClick: choisir("eurl") },
                        { icone: "buildings-linear", titre: t.sasu, detail: t.sasuDetail, onClick: choisir("sasu") },
                    ]}
                />
                <Pourquoi titre={t.pourquoi} texte={pourquoi("pStatut", "statut")} />
                <Passer onClick={() => (seule ? onQuitter?.() : suivante("statut"))} libelle={seule ? t.annuler : t.passer} />
            </Ecran>
        );
    }

    if (etape === "eurlFiscal") {
        const choisir = (s) => () => {
            enregistrer({ statut: s });
            suivante("statut");
        };
        return (
            <Ecran key={etape} {...communsEcran} phase={PHASES.eurlFiscal} titre={t.qEurl} aide={t.qEurlAide}>
                <Grille
                    choix={[
                        { icone: "safe-square-linear", titre: t.eurlIs, detail: t.eurlIsDetail, onClick: choisir("eurl") },
                        { icone: "user-linear", titre: t.eurlIr, detail: t.eurlIrDetail, onClick: choisir("eurlIr") },
                        { icone: "question-circle-linear", titre: t.eurlInconnu, detail: t.eurlInconnuDetail, onClick: choisir("eurl") },
                    ]}
                />
            </Ecran>
        );
    }

    if (etape === "activite") {
        const choisir = (a) => () => {
            if (a === "services") {
                enregistrer({ activite: a });
                aller("artisan");
                return;
            }
            // L'inscription comme artisan ne vaut que pour les prestations.
            enregistrer({ activite: a, artisan: null });
            suivante("activite");
        };
        return (
            <Ecran key={etape} {...communsEcran} phase={PHASES.activite} titre={t.qActivite} aide={t.qActiviteAide}>
                <Grille
                    choix={[
                        { icone: "sale-linear", titre: t.vente, detail: t.venteDetail, onClick: choisir("vente") },
                        { icone: "pen-2-linear", titre: t.services, detail: t.servicesDetail, onClick: choisir("services") },
                        { icone: "brain-linear", titre: t.bnc, detail: t.bncDetail, onClick: choisir("bnc") },
                        { icone: "medal-ribbon-star-linear", titre: t.cipav, detail: t.cipavDetail, onClick: choisir("cipav") },
                    ]}
                />
                <Pourquoi titre={t.pourquoi} texte={pourquoi("pActivite", "activite")} />
                <Passer onClick={() => (seule ? onQuitter?.() : suivante("activite"))} libelle={seule ? t.annuler : t.passer} />
            </Ecran>
        );
    }

    if (etape === "artisan") {
        const choisir = (oui) => () => {
            enregistrer({ artisan: oui });
            suivante("activite");
        };
        return (
            <Ecran key={etape} {...communsEcran} phase={PHASES.artisan} titre={t.qArtisan} aide={t.qArtisanAide}>
                <Grille
                    choix={[
                        { icone: "clipboard-check-linear", titre: t.artisanOui, detail: t.artisanOuiDetail, onClick: choisir(true) },
                        { icone: "shop-linear", titre: t.artisanNon, detail: t.artisanNonDetail, onClick: choisir(false) },
                    ]}
                />
            </Ecran>
        );
    }

    if (etape === "debut") {
        const projet = statut === "projet";
        const valider = () => {
            enregistrer({ dateDebut: saisie.dateDebut || null });
            suivante("debut");
        };
        return (
            <Ecran
                key={etape}
                {...communsEcran}
                phase={PHASES.debut}
                titre={projet ? t.qDebutProjet : t.qDebut}
                aide={projet ? t.qDebutProjetAide : enSociete ? t.qDebutAideSociete : t.qDebutAideMicro}
            >
                <form onSubmit={soumettre(valider, !saisie.dateDebut)} noValidate>
                <div className="max-w-xs">
                    <Champ
                        type="date"
                        libelle={t.dateDebut}
                        valeur={saisie.dateDebut}
                        poser={poserSaisie("dateDebut")}
                        // Une activité déjà lancée a commencé au plus tard aujourd'hui.
                        max={projet ? undefined : new Date().toISOString().slice(0, 10)}
                    />
                </div>
                <Avancer type="submit" disabled={!saisie.dateDebut}>
                    {seule ? t.enregistrer : t.continuer}
                </Avancer>
                </form>
                <Pourquoi titre={t.pourquoi} texte={pourquoi("pDebut", "debut")} />
                <Passer onClick={() => (seule ? onQuitter?.() : suivante("debut"))} libelle={seule ? t.annuler : t.passer} />
            </Ecran>
        );
    }

    if (etape === "ca") {
        const projet = statut === "projet";
        const anDernierPossible = !projet && (!profil.dateDebut || Number(profil.dateDebut.slice(0, 4)) < annee);
        const anneesAutres = Object.fromEntries(Object.entries(profil.encaissements || {}).filter(([m]) => !m.startsWith(prefixeAnnee)));
        const valider = () => {
            const caAnDernier = anDernierPossible ? lireMontant(saisie.caAnDernier) : null;
            if (grille) {
                // Les mois, et le rythme annuel qu'ils dessinent : c'est lui
                // que les outils lisent sous le nom de chiffre d'affaires.
                enregistrer({ encaissements: { ...anneesAutres, ...moisTapes }, caCetteAnnee: rythme.rythme, caAnDernier });
            } else {
                enregistrer({ encaissements: anneesAutres, caCetteAnnee: lireMontant(saisie.caCetteAnnee), caAnDernier });
            }
            suivante("ca");
        };
        const bloque = grille ? !rythme : lireMontant(saisie.caCetteAnnee) === null;
        const nomMois = (m, forme = "short") => {
            const x = new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "fr-FR", { month: forme }).format(new Date(annee, Number(m.slice(5, 7)) - 1, 1));
            return x.charAt(0).toUpperCase() + x.slice(1);
        };
        const lienBascule =
            !projet && moisPossibles.length > 0 ? (
                <button
                    type="button"
                    onClick={() => setParMois((x) => !x)}
                    className="mt-3 inline-flex min-h-[44px] items-center text-sm text-gray-600 dark:text-lightBlue underline underline-offset-2 decoration-gray-300 dark:decoration-white/20 hover:text-primary transition-colors duration-200"
                >
                    {grille ? t.versTotal : t.versMois}
                </button>
            ) : null;
        return (
            <Ecran
                key={`${etape}-${grille ? "mois" : "total"}`}
                {...communsEcran}
                phase={PHASES.ca}
                titre={grille ? (enSociete ? t.qCaMoisSociete : t.qCaMois) : projet ? t.qCaProjet : enSociete ? t.qCaSociete : t.qCa}
                aide={grille ? t.qCaMoisAide : projet ? t.qCaProjetAide : t.qCaAide}
            >
                <form onSubmit={soumettre(valider, bloque)} noValidate>
                {grille ? (
                    <>
                        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                            {moisPossibles.map((m) => (
                                <Champ
                                    key={m}
                                    libelle={m === `${annee}-${String(moisEnCours).padStart(2, "0")}` ? `${nomMois(m)} (${t.enCours})` : nomMois(m)}
                                    aria-label={`${nomMois(m, "long")} ${annee} (${t.euro})`}
                                    valeur={saisie.mois[m] ?? ""}
                                    poser={poserMois(m)}
                                    inputMode="numeric"
                                />
                            ))}
                        </div>
                        <p className="mt-4 mb-0 flex items-baseline justify-between border-t border-gray-200/70 dark:border-white/10 pt-3 text-sm">
                            <span className="text-gray-600 dark:text-lightBlue">{remplir(t.totalMois, { mois: nomMois(moisPossibles[0], "long").toLowerCase() })}</span>
                            <span className="font-semibold tabular-nums dark:text-white">{euros(rythme ? rythme.total : 0, lang)}</span>
                        </p>
                    </>
                ) : (
                    <Champ
                        libelle={`${t.caCetteAnnee} (${t.euro})`}
                        valeur={saisie.caCetteAnnee}
                        poser={poserSaisie("caCetteAnnee")}
                        inputMode="numeric"
                        placeholder="48000"
                    />
                )}
                {lienBascule}
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    {anDernierPossible && (
                        <Champ
                            libelle={`${t.caAnDernier} (${t.euro})`}
                            aide={t.caAnDernierAide}
                            valeur={saisie.caAnDernier}
                            poser={poserSaisie("caAnDernier")}
                            inputMode="numeric"
                        />
                    )}
                </div>
                <Apercu lignes={apercuCa} />
                <Avancer type="submit" disabled={bloque}>
                    {seule ? t.enregistrer : t.continuer}
                </Avancer>
                </form>
                <Pourquoi titre={t.pourquoi} texte={grille ? `${pourquoi("pCa", "ca")} ${t.pCaMois}` : pourquoi("pCa", "ca")} />
                <Passer onClick={() => (seule ? onQuitter?.() : suivante("ca"))} libelle={seule ? t.annuler : t.passer} />
            </Ecran>
        );
    }

    if (etape === "foyer") {
        const enCouple = profil.enCouple;
        const enfants = profil.enfants;
        const valider = () => {
            enregistrer({
                enCouple: enCouple ?? false,
                enfants: enfants ?? 0,
                autresRevenus: lireMontant(saisie.autresRevenus) ?? 0,
            });
            suivante("foyer");
        };
        return (
            <Ecran key={etape} {...communsEcran} phase={PHASES.foyer} titre={t.qFoyer} aide={t.qFoyerAide}>
                <form onSubmit={soumettre(valider, enCouple === null)} noValidate>
                <div className="space-y-5">
                    <div>
                        <span id="dossier-q-declarez" className="block text-xs text-gray-500 dark:text-lightBlue/70 mb-1.5">{t.declarez}</span>
                        {/* Deux grandes cases, comme sur la maquette. */}
                        <div role="group" aria-labelledby="dossier-q-declarez" className="grid grid-cols-2 gap-2">
                            {[
                                [false, t.seul],
                                [true, t.couple],
                            ].map(([valeur, libelle]) => (
                                <button
                                    key={libelle}
                                    type="button"
                                    aria-pressed={enCouple === valeur}
                                    onClick={() => enregistrer({ enCouple: valeur })}
                                    className={`min-h-[50px] rounded-xl border text-base transition-colors duration-200 ${
                                        enCouple === valeur
                                            ? "border-2 border-gray-900 dark:border-white font-semibold text-gray-900 dark:text-white"
                                            : "border-gray-200/70 dark:border-white/10 text-gray-700 dark:text-lightBlue hover:border-gray-300 dark:hover:border-white/25"
                                    }`}
                                >
                                    {libelle}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <span id="dossier-q-enfants" className="block text-xs text-gray-500 dark:text-lightBlue/70 mb-1.5">{t.enfants}</span>
                        {/* Un compteur −/+, comme sur la maquette. */}
                        <div role="group" aria-labelledby="dossier-q-enfants" className="flex items-center gap-4">
                            <button
                                type="button"
                                aria-label={t.enfantMoins}
                                disabled={(enfants ?? 0) <= 0}
                                onClick={() => enregistrer({ enfants: Math.max(0, (enfants ?? 0) - 1) })}
                                className="flex h-11 w-11 items-center justify-center rounded-full border border-gray-200/70 dark:border-white/10 text-gray-700 dark:text-lightBlue disabled:opacity-40"
                            >
                                <SolarIcon name="minus-linear" className="h-5 w-5" />
                            </button>
                            <output aria-live="polite" className="w-8 text-center text-xl font-semibold tabular-nums dark:text-white">
                                {enfants ?? 0}
                            </output>
                            <button
                                type="button"
                                aria-label={t.enfantPlus}
                                disabled={(enfants ?? 0) >= 10}
                                onClick={() => enregistrer({ enfants: Math.min(10, (enfants ?? 0) + 1) })}
                                className="flex h-11 w-11 items-center justify-center rounded-full border border-gray-200/70 dark:border-white/10 text-gray-900 dark:text-white disabled:opacity-40"
                            >
                                <SolarIcon name="add-linear" className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                    <div className="max-w-xs">
                        <Champ
                            libelle={`${t.autresRevenus} (${t.euro})`}
                            aide={t.autresRevenusAide}
                            valeur={saisie.autresRevenus}
                            poser={poserSaisie("autresRevenus")}
                            inputMode="numeric"
                        placeholder="0"
                        />
                    </div>
                </div>
                <Avancer type="submit" disabled={enCouple === null}>
                    {seule ? t.enregistrer : t.continuer}
                </Avancer>
                </form>
                <Pourquoi titre={t.pourquoi} texte={pourquoi("pFoyer", "foyer")} />
                <Passer onClick={() => (seule ? onQuitter?.() : suivante("foyer"))} libelle={seule ? t.annuler : t.passer} />
            </Ecran>
        );
    }

    if (etape === "objectif") {
        const valider = () => {
            enregistrer({
                objectifMensuel: lireMontant(saisie.objectifMensuel),
                fraisMensuels: lireMontant(saisie.fraisMensuels) ?? 0,
            });
            suivante("objectif");
        };
        return (
            <Ecran key={etape} {...communsEcran} phase={PHASES.objectif} titre={t.qObjectif} aide={t.qObjectifAide}>
                <form onSubmit={soumettre(valider, lireMontant(saisie.objectifMensuel) === null)} noValidate>
                <div className="grid gap-4 sm:grid-cols-2">
                    <Champ
                        libelle={`${t.objectif} (${t.euro})`}
                        valeur={saisie.objectifMensuel}
                        poser={poserSaisie("objectifMensuel")}
                        inputMode="numeric"
                        placeholder="2800"
                    />
                    <Champ
                        libelle={`${t.frais} (${t.euro})`}
                        aide={t.fraisAide}
                        valeur={saisie.fraisMensuels}
                        poser={poserSaisie("fraisMensuels")}
                        inputMode="numeric"
                        placeholder="0"
                    />
                </div>
                <Apercu lignes={apercuObjectif} />
                <Avancer type="submit" disabled={lireMontant(saisie.objectifMensuel) === null}>
                    {seule ? t.enregistrer : t.terminer}
                </Avancer>
                </form>
                <Pourquoi titre={t.pourquoi} texte={pourquoi("pObjectif", "objectif")} />
                <Passer onClick={() => (seule ? onQuitter?.() : suivante("objectif"))} libelle={seule ? t.annuler : t.passer} />
            </Ecran>
        );
    }

    return null;
}
